import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ServiceModal } from '@/components/ServiceModal';
import { VehicleDetailLeadForm } from './VehicleDetailLeadForm';
import { VehicleDetailFinancing } from './VehicleDetailFinancing';
import { openWhatsApp } from '@/lib/whatsapp';
import { getDistanceToStore, formatDistance } from '@/lib/geolocation';
import {
  Gauge,
  MessageCircle,
  Navigation,
  Phone,
  Shield,
} from 'lucide-react';

interface VehicleDetailSidebarProps {
  vehicle: any;
  config: any;
  vehicleStore: any;
  availableStoresCount: number;
  userLocation: { lat: number; lng: number } | null;
  onRequestLocation: () => void;
  buttonColor: string;
  primaryColor: string;
  secondaryColor: string;
  showTestDrive: boolean;
  testDriveService: any;
  financingService: any;
  onWhatsApp: () => void;
}

export function VehicleDetailSidebar({
  vehicle,
  config,
  vehicleStore,
  availableStoresCount,
  userLocation,
  onRequestLocation,
  buttonColor,
  primaryColor,
  secondaryColor,
  showTestDrive,
  testDriveService,
  financingService,
  onWhatsApp,
}: VehicleDetailSidebarProps) {
  const distance =
    availableStoresCount > 1 && userLocation
      ? getDistanceToStore(userLocation, vehicleStore)
      : null;

  const leadType = vehicleStore?.ad?.lead?.type;
  const showLeadForm = leadType === 'api' || leadType === 'email';

  const handleWhatsAppClick = () => {
    if (vehicleStore?.whatsapp?.ios) {
      openWhatsApp(vehicleStore.whatsapp.ios);
    }
  };

  const handlePhoneClick = () => {
    if (vehicleStore?.phone?.ios) {
      window.open(vehicleStore.phone.ios, '_blank');
    }
  };

  return (
    <aside className="wl7-vd-sidebar">
      <div className="wl7-vd-sidebar-card">
        {availableStoresCount > 1 && vehicleStore?.company && (
          <div className="wl7-vd-store-info">
            <div className="wl7-vd-store-info__head">
              <span className="wl7-vd-store-info__name">{vehicleStore.company}</span>
              {distance !== null && (
                <Badge
                  variant="secondary"
                  className="flex items-center gap-1 px-2 py-0.5 text-white font-medium flex-shrink-0"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Navigation className="h-3 w-3" />
                  <span className="text-xs">{formatDistance(distance)}</span>
                </Badge>
              )}
              {distance === null && !userLocation && (
                <button
                  type="button"
                  onClick={onRequestLocation}
                  className="text-xs font-medium underline"
                  style={{ color: primaryColor }}
                >
                  Ver distância
                </button>
              )}
            </div>
          </div>
        )}

        <div className="wl7-vd-sidebar__contact">
          <p className="wl7-vd-sidebar__contact-title">Fale conosco</p>

          {showLeadForm ? (
            <VehicleDetailLeadForm
              vehicle={vehicle}
              config={config}
              buttonColor={buttonColor}
              store={vehicleStore}
              serviceType="message"
              serviceTitle="Envie uma mensagem"
              service={{ lead: vehicleStore.ad.lead }}
              variant="sidebar"
            />
          ) : (
            <Button
              className="w-full font-semibold"
              style={{ background: buttonColor, color: 'var(--wl7-on-button, #fff)' }}
              onClick={onWhatsApp}
              size="lg"
            >
              <MessageCircle className="h-5 w-5 mr-2" />
              Enviar mensagem
            </Button>
          )}
        </div>

        <div className="wl7-vd-sidebar-actions">
          {showLeadForm && vehicleStore?.whatsapp?.ios && (
            <Button
              onClick={handleWhatsAppClick}
              variant="outline"
              size="lg"
              className="w-full border-2 border-green-600/30 text-green-700 hover:bg-green-50 transition-all duration-300"
            >
              <MessageCircle className="h-5 w-5 mr-2" />
              WhatsApp
            </Button>
          )}

          <Button
            onClick={handlePhoneClick}
            variant="outline"
            size="lg"
            className="w-full border-2 transition-all duration-300 shadow-sm hover:shadow-md"
            style={{ borderColor: `${primaryColor}40`, color: primaryColor }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = primaryColor;
              e.currentTarget.style.backgroundColor = `${primaryColor}10`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = `${primaryColor}40`;
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Phone className="h-5 w-5 mr-2" />
            Ligar
          </Button>
        </div>

        {(financingService || showTestDrive) && (
          <div className="wl7-vd-sidebar-services">
            <p className="wl7-vd-sidebar__contact-title">Serviços</p>
            <div className="wl7-vd-sidebar-actions">
              <VehicleDetailFinancing
                vehicle={vehicle}
                config={config}
                primaryColor={primaryColor}
                financingService={financingService}
              />

              {showTestDrive && (
                <ServiceModal
                  service={testDriveService}
                  serviceTitle="Test-Drive"
                  config={config}
                  preSelectedVehicle={vehicle}
                >
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full border-2 transition-all duration-300 shadow-sm hover:shadow-md"
                    style={{ borderColor: `${primaryColor}40`, color: primaryColor }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = primaryColor;
                      e.currentTarget.style.backgroundColor = `${primaryColor}10`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = `${primaryColor}40`;
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Gauge className="h-5 w-5 mr-2" />
                    Agendar test-drive
                  </Button>
                </ServiceModal>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        className="wl7-vd-sidebar-card wl7-vd-sidebar-card--guarantee"
        style={{ background: `linear-gradient(135deg, ${secondaryColor}, ${secondaryColor}dd)` }}
      >
        <Shield className="h-12 w-12 mx-auto mb-3 opacity-90" />
        <h3 className="font-bold mb-2">Garantia e Qualidade</h3>
        <p className="text-sm opacity-90">
          Todos os nossos veículos passam por rigorosa inspeção e vêm com garantia.
        </p>
      </div>
    </aside>
  );
}
